import { z } from 'zod';

/**
 * La forme de cible unique des outils de mise en forme du canvas (spec #1858).
 *
 * WHY une seule forme : avant elle, chaque verbe avait sa syntaxe (`selector`,
 * `from`/`to`, `scope`) et aucun ne savait viser plusieurs paragraphes en un
 * appel. Les outils de forme — lecture (`canvas_inspect` `formatOf`), puis
 * `format_text`, `format_paragraphs`, `copy_format` — partagent CES schémas :
 * l'agent n'apprend qu'une syntaxe, et un outil nouveau ne redéfinit rien.
 *
 * Les identifiants sont ceux que publie une lecture du document (`nodeId`). Le
 * schéma ne vérifie que la forme : l'existence d'un identifiant se juge contre
 * le document vivant, dans le navigateur, qui nomme ceux qu'il ne trouve pas.
 *
 * ⚠️ Ces schémas ne portent pas de `.describe()`, sauf le texte d'un passage
 * (#1870). Le schéma livré au modèle recopie la cible à chaque emploi
 * (`$refStrategy:'none'`) : une description posée ici était lue quatre fois
 * (`format_text`, `format_paragraphs`, `copy_format`, `formatOf`). Ce que
 * chaque forme désigne est dit UNE fois, par la ligne de `format_text` dans la
 * référence des verbes (`actionCatalog.ts`, `llmDescription`).
 */
const IdentifiantDeParagrapheSchema = z.string().min(1);

/**
 * Les trois zones d'un en-tête ou d'un pied de page, dans le vocabulaire de
 * `set_header_footer` (`actionCatalog.ts`, qui dit pourquoi ce ne sont pas les
 * mots de l'éditeur).
 */
export const CanvasPageZoneSchema = z.enum(['primary', 'first_page', 'even_pages']);

/**
 * Un LIEU hors du corps de l'acte : l'en-tête ou le pied d'une zone, ou les
 * notes de bas de page — une seule par son `note_id` (celui que lisent
 * `edit_footnote` et `delete_footnote`), toutes sans lui.
 *
 * WHY une dimension de la cible et pas un verbe par lieu : l'éditeur met en
 * forme un en-tête par la même opération que le corps, à une adresse près. Un
 * verbe par lieu recopierait réglages, reçu et vérification.
 */
export const CanvasFormatPlaceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.enum(['header', 'footer']), zone: CanvasPageZoneSchema }).strict(),
  z.object({ kind: z.literal('footnote'), note_id: z.string().min(1).optional() }).strict(),
]);
export type CanvasFormatPlace = z.infer<typeof CanvasFormatPlaceSchema>;

/**
 * Une liste et une plage nomment des paragraphes du CORPS : un lieu n'y a pas
 * de sens. Sans ce message, le refus d'une clé en trop ne dirait pas à l'agent
 * quelle cible prend un lieu.
 */
const SANS_LIEU = 'a list or a range designates body paragraphs by nodeId and takes no other key. '
  + 'To target a header, a footer or footnotes, use {kind:"all", in} or {kind:"text", text, in}.';

const CibleToutLActeSchema = z
  .object({ kind: z.literal('all'), in: CanvasFormatPlaceSchema.optional() })
  .strict();

const CibleListeSchema = z
  .object({
    kind: z.literal('ids'),
    nodeIds: z.array(IdentifiantDeParagrapheSchema).min(1),
  })
  .strict(SANS_LIEU);

const CiblePlageSchema = z
  .object({
    kind: z.literal('range'),
    from: IdentifiantDeParagrapheSchema,
    to: IdentifiantDeParagrapheSchema,
  })
  .strict(SANS_LIEU);

/**
 * Nombre maximal de textes exacts qu'une cible « texte » sans paragraphe
 * désigne en un appel (#1947). Valeur de jugement : la taille d'un plan
 * (`apply_edit_plan`, 50 étapes), que cette liste remplace quand la demande
 * est « la même forme sur ces textes-là ».
 */
export const CANVAS_FORMAT_TEXTS_MAX = 50;

const TexteExactSchema = z.string().min(1);

/**
 * Un texte exact. Avec `nodeId` : UN passage de ce paragraphe. Sans (#1947) :
 * toutes les occurrences du texte dans l'acte — l'agent n'a plus à les lister
 * une à une, ce qui en laissait échapper sans qu'aucun reçu le dise.
 *
 * Sans `nodeId`, `text` prend aussi une LISTE de textes exacts : « toutes les
 * dates en rouge » tient alors en un appel, là où un texte par appel en
 * coûtait vingt-deux (mesuré dans l'application, 2026-10-05). Une liste avec
 * `nodeId` est refusée par `appliquerLesReglesInterChamps` (`actionCatalog.ts`) :
 * une branche d'union discriminée doit rester un objet nu. Même refus, au même
 * endroit, pour `nodeId` avec `in` : un `nodeId` nomme un paragraphe du corps.
 */
const CiblePassageSchema = z
  .object({
    kind: z.literal('text'),
    nodeId: IdentifiantDeParagrapheSchema.optional(),
    text: z.union([TexteExactSchema, z.array(TexteExactSchema).min(1).max(CANVAS_FORMAT_TEXTS_MAX)]).describe('Texte exact du passage.'),
    in: CanvasFormatPlaceSchema.optional(),
  })
  .strict();

/**
 * Le lieu que vise une cible ; `undefined` : le corps de l'acte. Seul point de
 * lecture de `in` : une liste et une plage n'en portent pas.
 */
export function lieuDeLaCible(cible: CanvasTarget): CanvasFormatPlace | undefined {
  return cible.kind === 'all' || cible.kind === 'text' ? cible.in : undefined;
}

/**
 * Cible de PARAGRAPHES : tout l'acte (ou tout un lieu), une liste, ou une plage. C'est la cible
 * des outils qui règlent ou lisent un paragraphe entier.
 */
export const CanvasParagraphTargetSchema = z.discriminatedUnion('kind', [CibleToutLActeSchema, CibleListeSchema, CiblePlageSchema]);
export type CanvasParagraphTarget = z.infer<typeof CanvasParagraphTargetSchema>;

/**
 * Cible d'une COPIE de forme (`copy_format`, #1869) : une liste ou une plage.
 * « Tout l'acte » n'en est pas une : le paragraphe modèle en fait partie.
 */
export const CanvasCopyFormatTargetSchema = z.discriminatedUnion('kind', [CibleListeSchema, CiblePlageSchema]);
export type CanvasCopyFormatTarget = z.infer<typeof CanvasCopyFormatTargetSchema>;

/**
 * Cible complète : les trois cibles de paragraphes, plus le texte exact — un
 * passage d'un paragraphe, ou toutes ses occurrences dans l'acte. Le texte
 * n'a de sens que pour un outil qui règle du TEXTE (`format_text`).
 */
export const CanvasTargetSchema = z.discriminatedUnion('kind', [
  CibleToutLActeSchema,
  CibleListeSchema,
  CiblePlageSchema,
  CiblePassageSchema,
]);
export type CanvasTarget = z.infer<typeof CanvasTargetSchema>;
